import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useTeamChatState } from "@/lib/team-chat-state";
import { useAuth } from "@/lib/auth-context";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  MessagesSquare, X, Send, ArrowLeft, Plus, Users, User,
  GripHorizontal, Hash, Loader2, Phone, Volume2, VolumeX,
} from "lucide-react";
import { useNotificationSound, useMessageSoundAlert } from "@/hooks/use-notification-sound";
import { usePlivo } from "@/lib/plivo-context";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { getTeamChatPusherChannel, releaseTeamChatPusherChannel, subscribeToReconnect } from "@/lib/pusher-client";

type ChannelMember = { userId: string; displayName: string };
type ChatMessage = {
  id: string;
  channelId: string;
  senderUserId: string;
  senderDisplayName: string;
  body: string;
  createdAt: string;
  isDeleted: boolean;
};
type ChatChannel = {
  id: string;
  tenantId: string;
  name: string | null;
  type: string;
  createdBy: string | null;
  createdAt: string;
  members: ChannelMember[];
  lastMessage: ChatMessage | null;
  unreadCount: number;
};

export function TeamChatHeaderButton() {
  const { toggle, isOpen } = useTeamChatState();
  const prevUnreadRef = useRef(0);
  const [wiggleKey, setWiggleKey] = useState(0);
  const { data: unreadData } = useQuery<{ total: number }>({
    queryKey: ["/api/team-chat/unread"],
    refetchInterval: 300000,
  });

  const unreadTotal = unreadData?.total || 0;
  const hasUnread = unreadTotal > 0;

  useEffect(() => {
    if (unreadTotal > prevUnreadRef.current && unreadTotal > 0) {
      setWiggleKey(k => k + 1);
    }
    prevUnreadRef.current = unreadTotal;
  }, [unreadTotal]);

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      className={`relative h-8 w-8 ${isOpen ? "text-primary" : ""} ${hasUnread && !isOpen ? "text-orange-500 dark:text-orange-400" : ""}`}
      title="Team Chat"
      data-testid="button-team-chat-toggle"
    >
      <MessagesSquare
        key={wiggleKey}
        className={`h-4 w-4 ${hasUnread && !isOpen ? "team-chat-wiggle" : ""}`}
      />
      {hasUnread && (
        <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center animate-pulse">
          {unreadTotal > 9 ? "9+" : unreadTotal}
        </span>
      )}
    </Button>
  );
}

function extractPhoneFromCallRequest(body: string): string | null {
  const match = body.match(/Call Request:.*?\((\+?\d[\d\s-]+)\)/);
  return match ? match[1].replace(/[\s-]/g, "") : null;
}

export function FloatingTeamChat() {
  const { isOpen, close, selectedChannelId, selectChannel } = useTeamChatState();
  const { user } = useAuth();
  const { makeCall, isRegistered, callState } = usePlivo();
  const { enabled: soundEnabled, toggle: toggleSound } = useNotificationSound("team_chat");
  const [messageInput, setMessageInput] = useState("");
  const [typingUsers, setTypingUsers] = useState<Map<string, string>>(new Map());
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [showNewDm, setShowNewDm] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const channelRef = useRef<any>(null);
  const typingTimeoutRef = useRef<any>(null);
  const dragConstraintsRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const { data: channels, isLoading: channelsLoading } = useQuery<ChatChannel[]>({
    queryKey: ["/api/team-chat/channels"],
    enabled: isOpen,
    refetchInterval: 300000,
  });

  const { data: messages, isLoading: messagesLoading } = useQuery<ChatMessage[]>({
    queryKey: ["/api/team-chat/channels", selectedChannelId, "messages"],
    queryFn: async () => {
      const r = await fetch(`/api/team-chat/channels/${selectedChannelId}/messages?limit=100`, { credentials: "include" });
      if (!r.ok) throw new Error(`Failed to fetch messages: ${r.status}`);
      const data = await r.json();
      return Array.isArray(data) ? data : [];
    },
    enabled: !!selectedChannelId && isOpen,
  });

  useMessageSoundAlert("team_chat", messages, soundEnabled, user?.id, selectedChannelId);

  const { data: tenantUsers } = useQuery<any[]>({
    queryKey: ["/api/users"],
    enabled: showNewChannel || showNewDm,
  });

  const sendMutation = useMutation({
    mutationFn: async (body: string) => {
      const res = await apiRequest("POST", `/api/team-chat/channels/${selectedChannelId}/messages`, { body });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-chat/channels", selectedChannelId, "messages"] });
      queryClient.invalidateQueries({ queryKey: ["/api/team-chat/channels"] });
    },
  });

  const createChannelMutation = useMutation({
    mutationFn: async () => {
      if (selectedMembers.length === 1 && !newChannelName.trim()) {
        const res = await apiRequest("POST", "/api/team-chat/direct", { targetUserId: selectedMembers[0] });
        return res.json();
      }
      const res = await apiRequest("POST", "/api/team-chat/channels", { name: newChannelName || `Group Chat`, memberUserIds: selectedMembers });
      return res.json();
    },
    onSuccess: (channel: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-chat/channels"] });
      queryClient.invalidateQueries({ queryKey: ["/api/team-chat/unread"] });
      setShowNewChannel(false);
      setNewChannelName("");
      setSelectedMembers([]);
      selectChannel(channel.id);
    },
    onError: (err: any) => {
      console.error("Failed to create channel:", err);
    },
  });

  const createDmMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      const res = await apiRequest("POST", "/api/team-chat/direct", { targetUserId });
      return res.json();
    },
    onSuccess: (channel: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-chat/channels"] });
      setShowNewDm(false);
      selectChannel(channel.id);
    },
  });

  const markReadMutation = useMutation({
    mutationFn: async (channelId: string) => {
      await apiRequest("POST", `/api/team-chat/channels/${channelId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-chat/channels"] });
      queryClient.invalidateQueries({ queryKey: ["/api/team-chat/unread"] });
    },
  });

  useEffect(() => {
    if (!isOpen || !selectedChannelId) return;
    const channel = getTeamChatPusherChannel(selectedChannelId);
    channelRef.current = channel;

    const handleMessage = (msg: ChatMessage) => {
      if (msg.channelId === selectedChannelId) {
        queryClient.invalidateQueries({ queryKey: ["/api/team-chat/channels", selectedChannelId, "messages"] });
        markReadMutation.mutate(selectedChannelId);
      }
      queryClient.invalidateQueries({ queryKey: ["/api/team-chat/channels"] });
      queryClient.invalidateQueries({ queryKey: ["/api/team-chat/unread"] });
    };

    const handleTyping = (data: { channelId: string; userId: string; displayName: string }) => {
      if (data.channelId === selectedChannelId && data.userId !== user?.id) {
        setTypingUsers(prev => new Map(prev).set(data.userId, data.displayName));
        setTimeout(() => {
          setTypingUsers(prev => {
            const next = new Map(prev);
            next.delete(data.userId);
            return next;
          });
        }, 3000);
      }
    };

    const handleChannelDeleted = (data: { channelId: string }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-chat/channels"] });
      if (data.channelId === selectedChannelId) {
        selectChannel(null);
      }
    };

    if (channel) {
      channel.bind("team-chat-message", handleMessage);
      channel.bind("team-chat-typing", handleTyping);
      channel.bind("team-chat-channel-deleted", handleChannelDeleted);
    }

    markReadMutation.mutate(selectedChannelId);

    const unsubReconnect = subscribeToReconnect(() => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-chat/channels", selectedChannelId, "messages"] });
    });

    return () => {
      if (channel) {
        channel.unbind("team-chat-message", handleMessage);
        channel.unbind("team-chat-typing", handleTyping);
        channel.unbind("team-chat-channel-deleted", handleChannelDeleted);
      }
      releaseTeamChatPusherChannel(selectedChannelId);
      channelRef.current = null;
      unsubReconnect();
    };
  }, [selectedChannelId, isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    if (!messageInput.trim() || !selectedChannelId) return;
    sendMutation.mutate(messageInput.trim());
    setMessageInput("");
  };

  const typingDebounceRef2 = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleTyping = () => {
    if (!selectedChannelId) return;
    if (typingDebounceRef2.current) return;
    typingDebounceRef2.current = setTimeout(() => {
      typingDebounceRef2.current = null;
    }, 2000);
    fetch(`/api/team-chat/channels/${selectedChannelId}/typing`, {
      method: "POST",
      credentials: "include",
    }).catch(() => {});
  };

  const getChannelDisplayName = (channel: ChatChannel) => {
    if (channel.type === "group" || channel.type === "public") return channel.name || (channel.type === "public" ? "General" : "Group");
    const otherMember = channel.members.find(m => m.userId !== user?.id);
    return otherMember?.displayName || "Direct Message";
  };

  const selectedChannel = channels?.find(c => c.id === selectedChannelId);
  const sortedMessages = Array.isArray(messages) ? [...messages].sort((a, b) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  ) : [];

  return (
    <>
      <div ref={dragConstraintsRef} className="fixed inset-0 pointer-events-none z-40" />
      <AnimatePresence>
        {isOpen && (
          <motion.div
            drag
            dragConstraints={dragConstraintsRef}
            dragMomentum={false}
            dragElastic={0}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={() => setTimeout(() => setIsDragging(false), 50)}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-4 right-4 z-50"
            style={{ touchAction: "none" }}
          >
      <div
        className="w-[380px] h-[520px] rounded-lg shadow-2xl border bg-background flex flex-col overflow-hidden"
        data-testid="floating-team-chat"
      >
        <div
          className="flex items-center justify-between px-3 py-2 border-b bg-muted/50 cursor-grab active:cursor-grabbing select-none"
        >
          <div className="flex items-center gap-2">
            <GripHorizontal className="h-4 w-4 text-muted-foreground" />
            {(showNewChannel || showNewDm) ? (
              <>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setShowNewChannel(false); setShowNewDm(false); setNewChannelName(""); setSelectedMembers([]); }} data-testid="button-chat-back">
                  <ArrowLeft className="h-3 w-3" />
                </Button>
                <span className="font-medium text-sm">Team Chat</span>
              </>
            ) : selectedChannelId && selectedChannel ? (
              <>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => selectChannel(null)} data-testid="button-chat-back">
                  <ArrowLeft className="h-3 w-3" />
                </Button>
                <span className="font-medium text-sm truncate max-w-[200px]">
                  {selectedChannel.type === "group" ? <Hash className="h-3 w-3 inline mr-1" /> : <User className="h-3 w-3 inline mr-1" />}
                  {getChannelDisplayName(selectedChannel)}
                </span>
              </>
            ) : (
              <span className="font-medium text-sm">Team Chat</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {!selectedChannelId && !showNewChannel && !showNewDm && (
              <>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowNewDm(true)} data-testid="button-new-dm" title="New DM">
                  <User className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowNewChannel(true)} data-testid="button-new-channel" title="New Channel">
                  <Plus className="h-3 w-3" />
                </Button>
              </>
            )}
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={toggleSound} data-testid="button-toggle-sound-team-chat" title={soundEnabled ? "Mute notifications" : "Unmute notifications"}>
              {soundEnabled ? <Volume2 className="h-3 w-3" /> : <VolumeX className="h-3 w-3 text-muted-foreground" />}
            </Button>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={close} data-testid="button-close-chat">
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {showNewChannel ? (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="px-3 py-2 border-b">
              <h3 className="text-sm font-semibold">New Conversation</h3>
            </div>
            <ScrollArea className="flex-1 p-3">
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Channel Name {selectedMembers.length <= 1 ? <span className="text-muted-foreground">(optional for DM)</span> : null}</Label>
                  <Input value={newChannelName} onChange={e => setNewChannelName(e.target.value)} placeholder="e.g. sales-team" className="h-8 text-sm mt-1" data-testid="input-channel-name" />
                </div>
                <div>
                  <Label className="text-xs">Members</Label>
                  <div className="max-h-[280px] overflow-y-auto space-y-1 mt-1 border rounded-md p-2">
                    {tenantUsers?.filter((u: any) => u.id !== user?.id && u.isActive !== false).map((u: any) => (
                      <label key={u.id} className="flex items-center gap-2 cursor-pointer text-sm py-1">
                        <Checkbox
                          checked={selectedMembers.includes(u.id)}
                          onCheckedChange={(checked) => {
                            setSelectedMembers(prev => checked ? [...prev, u.id] : prev.filter(id => id !== u.id));
                          }}
                        />
                        {u.displayName || u.email}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollArea>
            <div className="border-t p-2 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => { setShowNewChannel(false); setNewChannelName(""); setSelectedMembers([]); }}>Cancel</Button>
              <Button size="sm" onClick={() => createChannelMutation.mutate()} disabled={selectedMembers.length === 0 || (selectedMembers.length > 1 && !newChannelName.trim()) || createChannelMutation.isPending} data-testid="button-create-channel">
                {createChannelMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Start
              </Button>
            </div>
          </div>
        ) : showNewDm ? (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="px-3 py-2 border-b">
              <h3 className="text-sm font-semibold">New Direct Message</h3>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-1">
                {tenantUsers?.filter((u: any) => u.id !== user?.id && u.isActive !== false).map((u: any) => (
                  <button
                    key={u.id}
                    className="w-full text-left px-3 py-2 rounded hover:bg-muted transition-colors flex items-center gap-2"
                    onClick={() => createDmMutation.mutate(u.id)}
                    data-testid={`dm-user-${u.id}`}
                  >
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary">
                      {(u.displayName || u.email || "?")[0].toUpperCase()}
                    </div>
                    <span className="text-sm">{u.displayName || u.email}</span>
                  </button>
                ))}
              </div>
            </ScrollArea>
            <div className="border-t p-2 flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowNewDm(false)}>Cancel</Button>
            </div>
          </div>
        ) : !selectedChannelId ? (
          <ScrollArea className="flex-1">
            {channelsLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : !channels || channels.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center gap-2">
                <MessagesSquare className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No conversations yet</p>
                <p className="text-xs text-muted-foreground">Start a new DM or create a channel</p>
              </div>
            ) : (
              <div className="divide-y">
                {channels.map(ch => (
                  <button
                    key={ch.id}
                    type="button"
                    className="w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors flex items-center gap-2 cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); selectChannel(ch.id); }}
                    data-testid={`channel-item-${ch.id}`}
                  >
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                      {ch.type === "group" ? <Users className="h-4 w-4 text-primary" /> : <User className="h-4 w-4 text-primary" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium truncate">{getChannelDisplayName(ch)}</span>
                        {ch.unreadCount > 0 && (
                          <Badge variant="destructive" className="ml-1 h-5 min-w-[20px] text-[10px] px-1.5">{ch.unreadCount}</Badge>
                        )}
                      </div>
                      {ch.lastMessage && (
                        <p className="text-xs text-muted-foreground truncate">
                          {ch.lastMessage.senderDisplayName}: {ch.lastMessage.body}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        ) : (
          <>
            <ScrollArea className="flex-1 p-3">
              {messagesLoading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : sortedMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <p className="text-sm text-muted-foreground">No messages yet. Say hello!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {sortedMessages.map((msg, i) => {
                    const isMe = msg.senderUserId === user?.id;
                    const showName = !isMe && (i === 0 || sortedMessages[i - 1]?.senderUserId !== msg.senderUserId);
                    return (
                      <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`} data-testid={`message-${msg.id}`}>
                        <div className={`max-w-[80%] ${isMe ? "items-end" : "items-start"}`}>
                          {showName && (
                            <p className="text-[10px] text-muted-foreground mb-0.5 px-1">{msg.senderDisplayName}</p>
                          )}
                          <div className={`px-3 py-1.5 rounded-lg text-sm ${isMe ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                            {msg.body}
                            {(() => {
                              const phone = extractPhoneFromCallRequest(msg.body);
                              if (!phone) return null;
                              return (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="mt-2 w-full text-xs gap-1 border-green-300 text-green-600 bg-white hover:bg-green-50 dark:bg-transparent dark:border-green-600 dark:text-green-400"
                                  onClick={() => {
                                    if (isRegistered && callState === "idle") {
                                      makeCall(phone);
                                    }
                                  }}
                                  disabled={!isRegistered || callState !== "idle"}
                                  data-testid={`button-call-${phone}`}
                                >
                                  <Phone className="w-3 h-3" />
                                  Call {phone}
                                </Button>
                              );
                            })()}
                          </div>
                          <p className="text-[9px] text-muted-foreground mt-0.5 px-1">
                            {new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
              {typingUsers.size > 0 && (
                <p className="text-xs text-muted-foreground italic mt-1">
                  {Array.from(typingUsers.values()).join(", ")} typing...
                </p>
              )}
            </ScrollArea>

            <div className="border-t p-2 flex gap-2">
              <Input
                value={messageInput}
                onChange={(e) => { setMessageInput(e.target.value); handleTyping(); }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="Type a message..."
                className="text-sm h-8"
                data-testid="input-chat-message"
              />
              <Button size="icon" className="h-8 w-8 flex-shrink-0" onClick={handleSend} disabled={!messageInput.trim() || sendMutation.isPending} data-testid="button-send-message">
                <Send className="h-3.5 w-3.5" />
              </Button>
            </div>
          </>
        )}
      </div>
      </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
